import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API, BACKEND_URL, formatApiError } from "@/App";
import { useVideoUrl } from "@/hooks/useVideoUrl";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, 
  Upload, 
  ArrowRight,
  Loader2,
  Users,
  FileVideo,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Scissors,
  Plus,
} from "lucide-react";
import { toast } from "sonner";

const ProjectPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [collaboratorEmail, setCollaboratorEmail] = useState("");
  const [addingCollaborator, setAddingCollaborator] = useState(false);
  const [collaboratorDialogOpen, setCollaboratorDialogOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [cutdowns, setCutdowns] = useState([]);
  const [cutdownDialogOpen, setCutdownDialogOpen] = useState(false);
  const [cutdownName, setCutdownName] = useState("");
  const [cutdownSceneIds, setCutdownSceneIds] = useState([]);
  const [creatingCutdown, setCreatingCutdown] = useState(false);
  const [scenes, setScenes] = useState([]);

  const fetchProject = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    fetchProject();
    // Fetch cutdowns and scenes
    const loadExtras = async () => {
      try {
        const [cutdownsRes, scenesRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}/cutdowns`).catch(() => ({ data: [] })),
          axios.get(`${API}/projects/${projectId}/scenes`).catch(() => ({ data: [] })),
        ]);
        setCutdowns(cutdownsRes.data || []);
        setScenes(scenesRes.data || []);
      } catch {}
    };
    loadExtras();
  }, [fetchProject]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/projects/${projectId}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      toast.success("Video uploaded");
      fetchProject();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const analyzeVideo = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/analyze`);
      toast.success(`${response.data.scenes?.length || 0} scenes detected`);
      fetchProject();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAnalyzing(false);
    }
  };

  const addCollaborator = async (e) => {
    e.preventDefault();
    if (!collaboratorEmail.trim()) return;
    setAddingCollaborator(true);
    try {
      await axios.post(`${API}/projects/${projectId}/collaborators`, {
        project_id: projectId,
        email: collaboratorEmail
      });
      toast.success("Collaborator added");
      setCollaboratorEmail("");
      setCollaboratorDialogOpen(false);
      fetchProject();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddingCollaborator(false);
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setPlaying(!playing);
  };

  const createCutdown = async () => {
    if (!cutdownSceneIds.length) { toast.error("Select at least one scene"); return; }
    setCreatingCutdown(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/cutdowns`, {
        name: cutdownName || `${project.name} - Cutdown`,
        scene_ids: cutdownSceneIds,
      });
      toast.success(`Cutdown created: ${res.data.cutdown.name}`);
      setCutdowns((prev) => [...prev, res.data.cutdown]);
      setCutdownDialogOpen(false);
      setCutdownName("");
      setCutdownSceneIds([]);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCreatingCutdown(false); }
  };

  const toggleCutdownScene = (sceneId) => {
    setCutdownSceneIds((prev) =>
      prev.includes(sceneId) ? prev.filter((id) => id !== sceneId) : [...prev, sceneId]
    );
  };

  const { videoUrl: videoStreamUrl } = useVideoUrl(
    projectId, "source", !!project?.source_video
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="fixed inset-0 bg-gradient-hero -z-10" />
        <Loader2 className="w-6 h-6 animate-spin text-cyan-400/40" />
      </div>
    );
  }

  if (!project) return null;

  const steps = [
    { id: "upload", label: "Upload Video", desc: project.source_video ? `${project.video_info?.codec || 'Video'} - ${Math.round(project.video_duration || 0)}s` : "MOV, MP4, AVI, ProRes", done: !!project.source_video, action: () => fileInputRef.current?.click() },
    { id: "analyze", label: "Analyze Scenes", desc: project.analysis_status === "completed" ? `${project.scenes?.length || 0} scenes detected` : "AI-powered scene detection", done: project.analysis_status === "completed", disabled: !project.source_video, action: analyzeVideo, loading: analyzing },
    { id: "aspect", label: "Aspect Ratios", desc: project.aspect_ratios?.length ? project.aspect_ratios.join(", ") : "1:1, 9:16, 4:5 cropping", done: project.aspect_ratios?.length > 0, disabled: project.analysis_status !== "completed", action: () => navigate(`/project/${projectId}/aspect-ratio`) },
    { id: "restripe", label: "Restripe", desc: project.graphics?.length ? `${project.graphics.length} graphics` : "Supers, disclaimers, audio", done: project.graphics?.length > 0, disabled: project.analysis_status !== "completed", action: () => navigate(`/project/${projectId}/restripe`) },
    { id: "encode", label: "Encode & Export", desc: "H.264, ProRes, social media specs", done: project.paid, disabled: !project.source_video, action: () => navigate(`/project/${projectId}/encoding`) }
  ];

  return (
    <div className="min-h-screen" data-testid="project-page">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />

      {/* Header */}
      <header className="border-b border-cyan-500/10 backdrop-blur-sm bg-[#020a14]/60">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-6">
              <button onClick={() => navigate("/dashboard")} className="text-cyan-400/40 hover:text-cyan-300 transition-colors" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-['Rajdhani'] text-lg tracking-wide">{project.name}</h1>
                <p className="text-sm text-cyan-200/30">
                  {project.status || "Draft"} {project.auto_save_at ? `- Saved ${new Date(project.auto_save_at).toLocaleTimeString()}` : ""}
                </p>
              </div>
            </div>

            <Dialog open={collaboratorDialogOpen} onOpenChange={setCollaboratorDialogOpen}>
              <button onClick={() => setCollaboratorDialogOpen(true)} className="flex items-center gap-2 text-sm text-cyan-400/40 hover:text-cyan-300 transition-colors" data-testid="add-collaborator-btn">
                <Users className="w-4 h-4" />
                <span>{project.collaborators?.length || 0}</span>
              </button>
              <DialogContent className="bg-[#020a14] border-cyan-500/20 rounded-sm sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-['Rajdhani'] tracking-wide">COLLABORATORS</DialogTitle>
                </DialogHeader>
                <form onSubmit={addCollaborator} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-cyan-300/60">Email</Label>
                    <Input type="email" placeholder="colleague@company.com" value={collaboratorEmail} onChange={(e) => setCollaboratorEmail(e.target.value)} className="h-12 rounded-sm" required data-testid="collaborator-email-input" />
                  </div>
                  <Button type="submit" className="w-full h-12 rounded-sm btn-primary font-['Rajdhani'] tracking-wider" disabled={addingCollaborator} data-testid="add-collaborator-submit">
                    {addingCollaborator ? <Loader2 className="w-4 h-4 animate-spin" /> : "ADD"}
                  </Button>
                </form>
                {project.collaborators?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-cyan-500/10">
                    {project.collaborators.map((email) => (
                      <div key={email} className="text-sm text-cyan-200/60 py-2">{email}</div>
                    ))}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
        {/* Video Area */}
        <div className="mb-12">
          {project.source_video ? (
            <div className="relative">
              {videoStreamUrl ? (
                <div className="aspect-video video-frame rounded-sm overflow-hidden relative group">
                  <video
                    ref={videoRef}
                    src={videoStreamUrl}
                    className="w-full h-full object-contain bg-black"
                    onLoadedData={() => setVideoReady(true)}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    muted={muted}
                    data-testid="video-player"
                  />
                  {/* Controls overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-4">
                      <button onClick={togglePlay} className="text-white hover:text-cyan-300 transition-colors" data-testid="play-pause-btn">
                        {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                      </button>
                      <button onClick={() => setMuted(!muted)} className="text-white hover:text-cyan-300 transition-colors" data-testid="mute-btn">
                        {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                      </button>
                      <div className="flex-1 flex items-center gap-4 text-sm text-white/60">
                        <span>{Math.round(project.video_duration || 0)}s</span>
                        <span>-</span>
                        <span>{project.video_info?.width}x{project.video_info?.height}</span>
                        <span>-</span>
                        <span>{project.video_info?.codec?.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aspect-video video-frame rounded-sm flex items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 mx-auto text-cyan-400/40 animate-spin mb-4" />
                    <p className="text-cyan-200/60">Loading video...</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div 
              className="aspect-video drop-zone rounded-sm flex items-center justify-center cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-area"
            >
              {uploading ? (
                <div className="text-center w-64">
                  <Loader2 className="w-8 h-8 mx-auto text-cyan-400/40 mb-4 animate-spin" />
                  <p className="text-cyan-200/40 text-sm mb-4">Uploading... {uploadProgress}%</p>
                  <Progress value={uploadProgress} className="h-1" />
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="w-8 h-8 mx-auto text-cyan-500/30 mb-4" />
                  <p className="text-cyan-200/60 mb-1">Drop your video here or click to browse</p>
                  <p className="text-sm text-cyan-200/30">MOV, MP4, AVI, ProRes up to 2GB</p>
                </div>
              )}
            </div>
          )}
          <input ref={fileInputRef} type="file" accept="video/*,.mov,.mp4,.avi,.wmv,.prores" onChange={handleFileUpload} className="hidden" data-testid="file-input" />
        </div>

        {/* Workflow Steps */}
        <div className="border-t border-cyan-500/10 pt-12">
          <h2 className="text-sm text-cyan-400/60 mb-8 tracking-widest uppercase">Workflow</h2>
          <div className="space-y-0">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className={`group flex items-center justify-between py-6 border-b border-cyan-500/10 ${step.disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer hover:bg-cyan-500/[0.03]'} px-4 -mx-4 rounded-sm transition-colors`}
                onClick={() => !step.disabled && !step.loading && step.action()}
                data-testid={`workflow-step-${step.id}`}
              >
                <div className="flex items-center gap-6">
                  <span className="text-sm text-cyan-500/30 font-mono w-8">{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className="font-['Rajdhani'] text-lg tracking-wide">{step.label}</h3>
                    <p className="text-sm text-cyan-200/30 mt-0.5">{step.desc}</p>
                  </div>
                </div>
                {!step.disabled && (
                  <div className="flex items-center gap-3 text-cyan-400/40">
                    {step.loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : step.done ? (
                      <span className="text-sm text-green-400/60">Done</span>
                    ) : (
                      <span className="text-sm">Start</span>
                    )}
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Cutdowns Section */}
        {project.analysis_status === "completed" && scenes.length > 0 && (
          <div className="mt-12 pt-12 border-t border-cyan-500/10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-sm text-cyan-400/60 tracking-widest uppercase">Cutdowns</h2>
                <p className="text-xs text-cyan-200/25 mt-1">Create shorter versions from your master edit</p>
              </div>
              <Dialog open={cutdownDialogOpen} onOpenChange={setCutdownDialogOpen}>
                <Button
                  onClick={() => setCutdownDialogOpen(true)}
                  variant="outline"
                  className="h-9 px-4 rounded-sm border-cyan-500/20 text-sm font-['Rajdhani'] tracking-wider"
                  data-testid="create-cutdown-btn"
                >
                  <Scissors className="w-4 h-4 mr-2" /> CREATE CUTDOWN
                </Button>
                <DialogContent className="bg-[#020a14] border-cyan-500/20 rounded-sm sm:max-w-lg" aria-describedby="cutdown-desc">
                  <DialogHeader>
                    <DialogTitle className="font-['Rajdhani'] tracking-wide">CREATE CUTDOWN</DialogTitle>
                  </DialogHeader>
                  <p className="text-xs text-cyan-200/30 -mt-1" id="cutdown-desc">
                    Select scenes from the master to include. Crop positions and graphics will be auto-copied.
                  </p>
                  <div className="space-y-4 mt-3">
                    <div>
                      <Label className="text-sm text-cyan-300/60">Cutdown Name</Label>
                      <Input
                        value={cutdownName}
                        onChange={(e) => setCutdownName(e.target.value)}
                        placeholder={`${project.name} - 15s`}
                        className="h-10 rounded-sm mt-2"
                        data-testid="cutdown-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-cyan-300/60 mb-2 block">
                        Select Scenes ({cutdownSceneIds.length} of {scenes.length})
                      </Label>
                      <div className="space-y-1 max-h-[240px] overflow-y-auto pr-1">
                        {scenes.sort((a, b) => a.start_time - b.start_time).map((s, i) => (
                          <button
                            key={s.id}
                            onClick={() => toggleCutdownScene(s.id)}
                            className={`w-full text-left p-2.5 rounded-sm border transition-all ${
                              cutdownSceneIds.includes(s.id)
                                ? "border-cyan-500/40 bg-cyan-500/[0.08]"
                                : "border-cyan-500/10 hover:border-cyan-500/20"
                            }`}
                            data-testid={`cutdown-scene-${i}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className={`text-sm ${cutdownSceneIds.includes(s.id) ? "text-cyan-300" : "text-cyan-200/50"}`}>
                                Scene {i + 1}
                              </span>
                              <span className="text-xs text-cyan-200/30 font-mono">
                                {s.start_time.toFixed(1)}s — {s.end_time.toFixed(1)}s ({(s.end_time - s.start_time).toFixed(1)}s)
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                    {cutdownSceneIds.length > 0 && (
                      <p className="text-xs text-cyan-400/50">
                        Estimated duration: {scenes.filter((s) => cutdownSceneIds.includes(s.id)).reduce((t, s) => t + (s.end_time - s.start_time), 0).toFixed(1)}s
                      </p>
                    )}
                    <Button
                      onClick={createCutdown}
                      disabled={cutdownSceneIds.length === 0 || creatingCutdown}
                      className="w-full h-10 rounded-sm btn-primary font-['Rajdhani'] tracking-wider"
                      data-testid="create-cutdown-submit"
                    >
                      {creatingCutdown ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Scissors className="w-4 h-4 mr-2" />}
                      CREATE CUTDOWN
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            {cutdowns.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {cutdowns.map((cd) => (
                  <button
                    key={cd.id}
                    onClick={() => navigate(`/project/${cd.id}`)}
                    className="p-4 card-glass rounded-sm text-left hover:border-cyan-500/30 transition-all group"
                    data-testid={`cutdown-${cd.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm text-cyan-200/70 font-['Rajdhani'] tracking-wide group-hover:text-cyan-300 transition-colors">{cd.name}</h3>
                        <p className="text-xs text-cyan-200/30 mt-0.5">{cd.video_duration ? `${cd.video_duration.toFixed(1)}s` : ""} — {cd.scenes?.length || 0} scenes</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-cyan-400/20 group-hover:text-cyan-300 transition-colors" />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-cyan-200/20">No cutdowns yet</p>
            )}
          </div>
        )}

        {/* Project Stats */}
        <div className="mt-12 pt-12 border-t border-cyan-500/10">
          <h2 className="text-sm text-cyan-400/60 mb-8 tracking-widest uppercase">Project Info</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: "Duration", value: project.video_duration ? `${Math.round(project.video_duration)}s` : "--" },
              { label: "Scenes", value: project.scenes?.length || 0 },
              { label: "Aspect Ratios", value: project.aspect_ratios?.length || 0 },
              { label: "Graphics", value: project.graphics?.length || 0 }
            ].map(stat => (
              <div key={stat.label} className="card-glass rounded-sm p-6">
                <p className="text-sm text-cyan-200/30 mb-1">{stat.label}</p>
                <p className="text-2xl font-['Rajdhani'] text-cyan-300">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProjectPage;
